import React, { useState, useEffect, useRef } from 'react';
import { PasskeyError } from '../lib/webauthn';
import { logout } from '../lib/auth';
import AuthLayout from './AuthLayout';
import { Button } from '@mes-amis/galette-design-system';

const MAX_FAILED_ATTEMPTS = 10;

interface LockScreenProps {
  /** Callback to attempt unlock with the registered passkey */
  onUnlock: () => Promise<boolean>;

  /** Callback to logout instead of unlocking */
  onLogout: () => void;
}

/**
 * Lock screen component shown when the app is locked
 * Uses WebAuthn with automatic PIN/password fallback if biometrics unavailable
 */
export default function LockScreen({ onUnlock, onLogout }: LockScreenProps) {
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failureCount, setFailureCount] = useState(0);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [logoutConfirmation, setLogoutConfirmation] = useState('');
  const [rateLimitSeconds, setRateLimitSeconds] = useState(0);

  // Store interval ref for cleanup on unmount
  const rateLimitIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (rateLimitIntervalRef.current) {
        clearInterval(rateLimitIntervalRef.current);
        rateLimitIntervalRef.current = null;
      }
    };
  }, []);

  const handleUnlock = async () => {
    // Check rate limit
    if (rateLimitSeconds > 0) {
      setError(`Please wait ${rateLimitSeconds} seconds before trying again.`);
      return;
    }

    try {
      setIsUnlocking(true);
      setError(null);

      const success = await onUnlock();

      if (!success) {
        // Use functional update to avoid stale state issues
        setFailureCount(prev => {
          const newFailureCount = prev + 1;

          // Apply exponential backoff rate limiting: 2^(attempt-1) seconds
          const delaySeconds = Math.pow(2, newFailureCount - 1);

          if (newFailureCount >= MAX_FAILED_ATTEMPTS) {
            // Max failures reached - force logout for security
            setError('Too many failed attempts. Logging out for security.');
            setTimeout(() => {
              onLogout();
            }, 2000);
          } else {
            setError(`Authentication failed. ${MAX_FAILED_ATTEMPTS - newFailureCount} attempt(s) remaining.`);

            // Clear any existing interval
            if (rateLimitIntervalRef.current) {
              clearInterval(rateLimitIntervalRef.current);
            }

            // Start rate limit countdown
            setRateLimitSeconds(delaySeconds);
            rateLimitIntervalRef.current = setInterval(() => {
              setRateLimitSeconds(prev => {
                if (prev <= 1) {
                  if (rateLimitIntervalRef.current) {
                    clearInterval(rateLimitIntervalRef.current);
                    rateLimitIntervalRef.current = null;
                  }
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);
          }

          return newFailureCount;
        });
      }
    } catch (error) {
      // Use functional update to avoid stale state issues
      setFailureCount(prev => {
        const newFailureCount = prev + 1;

        // Apply exponential backoff rate limiting: 2^(attempt-1) seconds
        const delaySeconds = Math.pow(2, newFailureCount - 1);

        if (error instanceof PasskeyError) {
          if (error.code === 'NOT_SUPPORTED') {
            setError('Authentication is not available. Logging out.');
            setTimeout(() => {
              onLogout();
            }, 2000);
          } else if (error.code === 'NOT_ALLOWED') {
            setError('Authentication was cancelled. Please try again.');
          } else {
            setError(error.message);
          }
        } else {
          setError('An unexpected error occurred. Please try again.');
        }

        if (newFailureCount >= MAX_FAILED_ATTEMPTS) {
          setError('Too many failed attempts. Logging out for security.');
          setTimeout(() => {
            onLogout();
          }, 2000);
        } else {
          // Clear any existing interval
          if (rateLimitIntervalRef.current) {
            clearInterval(rateLimitIntervalRef.current);
          }

          // Start rate limit countdown
          setRateLimitSeconds(delaySeconds);
          rateLimitIntervalRef.current = setInterval(() => {
            setRateLimitSeconds(prev => {
              if (prev <= 1) {
                if (rateLimitIntervalRef.current) {
                  clearInterval(rateLimitIntervalRef.current);
                  rateLimitIntervalRef.current = null;
                }
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }

        return newFailureCount;
      });
    } finally {
      setIsUnlocking(false);
    }
  };

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = () => {
    if (logoutConfirmation.toLowerCase() === 'logout and wipe device') {
      logout();
    }
  };

  const handleCancelLogout = () => {
    setShowLogoutConfirm(false);
    setLogoutConfirmation('');
  };

  return (
    <AuthLayout className="z-50" maxWidth="max-w-md">
      {/* Heading */}
      <div className="flex flex-col gap-4">
        <h1 className="font-heading text-[28px] leading-8 text-black">
          Screen Locked
        </h1>
        <p className="text-sm leading-5 text-black">
          Your session is locked for security.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Rate Limit Message */}
        {rateLimitSeconds > 0 && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-sm text-yellow-800">
              Please wait {rateLimitSeconds} second{rateLimitSeconds !== 1 ? 's' : ''} before trying again.
            </p>
          </div>
        )}

        {/* Unlock Button */}
        <Button
          onClick={handleUnlock}
          disabled={isUnlocking || failureCount >= MAX_FAILED_ATTEMPTS || rateLimitSeconds > 0}
          className="w-full"
          variant="primary"
        >
          {isUnlocking ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Authenticating...
            </span>
          ) : (
            'Unlock'
          )}
        </Button>

        {/* Logout Option */}
        {!showLogoutConfirm ? (
          <button
            onClick={handleLogout}
            className="text-sm font-medium text-[#555] hover:text-black transition-colors"
          >
            Logout and Wipe Device
          </button>
        ) : (
          <div className="p-4 bg-red-50 border border-red-200 rounded">
            <p className="text-sm text-red-800 mb-3">
              <strong>Warning:</strong> Logout will delete all local data
              including unsynced changes.
            </p>
            <label
              htmlFor="lock-logout-confirm"
              className="block text-xs font-medium text-red-900 mb-2"
            >
              Type <code className="bg-red-100 px-1 rounded">logout and wipe device</code> to
              confirm:
            </label>
            <input
              id="lock-logout-confirm"
              type="text"
              value={logoutConfirmation}
              onChange={(e) => setLogoutConfirmation(e.target.value)}
              placeholder="Type 'logout and wipe device' to confirm"
              className="w-full px-3 py-2 mb-3 text-sm border border-red-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              autoFocus
            />
            <div className="flex flex-col gap-2">
              <Button
                variant="primary"
                destructive
                onClick={handleConfirmLogout}
                disabled={logoutConfirmation.toLowerCase() !== 'logout and wipe device'}
                className="w-full"
              >
                Confirm Logout and Wipe Device
              </Button>
              <Button variant="tertiary" onClick={handleCancelLogout} className="w-full">
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}
